import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import NewListingForm from '../../components/NewListingForm';
import Page from '../../components/Page';

import EntitySearchForm from '../../components/EntitySearchForm';
import Pagination from '../../components/Pagination';
import client from '../../lib/feathers';
import PrimaryButton from '../../components/PrimaryButton';
import { useDispatch, useSelector } from 'react-redux';
import { setKeywords, setListings } from '../../lib/listingsSlice';

const getInitials = (string) => {
  const words = string.split(" ");
  const prefixes = ["A","of","&","and","And","the", "The", "at", "At"]
  const filtered = words.filter((w) => !prefixes.includes(w));
  if (filtered.length === 1) {
    return filtered[0].slice(0,2).toUpperCase();
  }
  if (filtered.length > 1) {

    if (filtered[1]){
      return (filtered[0][0] + filtered[1][0]).toUpperCase();
    } else {
      return "dd";
    }
    
  }
}

const statusList = ['unverified', 'verified', 'claimed'];
const tierList = [
  { label: 'Tier 1', value: 0 },
  { label: 'Tier 2', value: 98 },
  { label: 'Tier 3', value: 111 },
  { label: 'Tier 4', value: 127 },
];

const statusBadgeStyles = {
  unverified: 'bg-gray-100 text-gray-800',
  verified: 'bg-green-100 text-green-800',
  claimed: 'bg-blue-100 text-blue-800',
  featured: 'bg-yellow-100 text-yellow-800',
};

const getTierLabel = (featureLevel) => {
  const tier = tierList.find(t => t.value === featureLevel);
  return tier ? tier.label : `Level ${featureLevel}`;
};

export default function Listings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(query.get('page') || 0);
  const [statusFilter, setStatusFilter] = useState();
  const [tierFilter, setTierFilter] = useState();
  const [showExcluded, setShowExcluded] = useState(false);
  const listings = useSelector(state => state.listings.results);
  const [total, setTotal] = useState(0);
  const keywords = useSelector(state => state.listings.keywords);
  // const [keywords, setKeywords] = useState(query.keywords);

  const setOnKeywords = e => {
    dispatch(setKeywords(e));
  };
  const getListings = async () => {
    try {
      const service = client.service('admin/listing');
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: {
          createdAt: -1,
        },
      };

      if (keywords && keywords.length) {
        // Multi-token AND-of-iLike so "wil val vin" matches "Willamette Valley
        // Vineyards" — same pattern Discover / libation-search / the client
        // listing-search modal use.
        const tokens = keywords.trim().split(/\s+/).filter(Boolean);
        q.$and = tokens.map((t) => ({ name: { $iLike: `%${t}%` } }));
      }

      if (statusFilter) {
        q.status = statusFilter;
      }

      if (tierFilter !== undefined && tierFilter !== null) {
        q.featureLevel = tierFilter;
      }

      // Filter excluded listings based on toggle state
      // When showExcluded is false: send isExcluded=false to filter out excluded listings
      // When showExcluded is true: don't send isExcluded at all to show everything
      if (!showExcluded) {
        q.isExcluded = false;
      }

      const result = await service.find({
        query: q,
      });

      dispatch(setListings(result.data));
      setTotal(result.total);
    } catch (e) {
      console.log('error', e);
    }
  };

  const onSearch = e => {
    e.preventDefault();
    setPage(0);
    getListings();
  };
  const navigateToPage = (p = 0, kw) => {
    if (p * limit > total || p * limit < 0) return;
    let url = `/admin/listings?page=${p}`;
    if (kw) {
      url += `&keywords=${kw}`;
    }
    navigate(url);
    setPage(p);
  };

  const onNewListing = listing => {
    navigate(`/admin/listings/${listing.id}/`);
  };


  const displayAddress = metaLoc => {
    let addy = '';
    if (metaLoc && metaLoc.city) {
      addy += metaLoc.city;
    }
    if (metaLoc && metaLoc.state) {
      if (addy.length > 0) {
        addy += ', ';
      }
      addy += metaLoc.state;
    }
    return addy;
  };

  useEffect(() => {
    getListings();
  }, [page, statusFilter, tierFilter, showExcluded]);

  return (
    <Page title="Listings">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Listings' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <PrimaryButton onClick={() => setShowCreateModal(true)}>Create New Listing</PrimaryButton>
        </div>
      </div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <EntitySearchForm onSearch={onSearch} setKeywords={setOnKeywords} keywords={keywords} />
        <h3 className="text-lg leading-6 font-medium text-gray-900">Listings ({total} Total)</h3>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 py-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</label>
          <div className="sm:hidden">
            <select
              onChange={(e) => { setStatusFilter(e.target.value || undefined); setPage(0); }}
              value={statusFilter || ''}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
            >
              <option value="">All</option>
              {statusList.map((s) => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="-mb-px flex space-x-4" aria-label="Status filter">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setStatusFilter(undefined); setPage(0); }}
                className={`capitalize whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${!statusFilter ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                All
              </a>
              {statusList.map((s) => (
                <a
                  key={s}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setStatusFilter(s); setPage(0); }}
                  className={`capitalize whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === s ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  {s}
                </a>
              ))}
            </nav>
          </div>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tier</label>
          <div className="sm:hidden">
            <select
              onChange={(e) => { setTierFilter(e.target.value === '' ? undefined : Number(e.target.value)); setPage(0); }}
              value={tierFilter !== undefined && tierFilter !== null ? tierFilter : ''}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
            >
              <option value="">All</option>
              {tierList.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="-mb-px flex space-x-4" aria-label="Tier filter">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setTierFilter(undefined); setPage(0); }}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${tierFilter === undefined || tierFilter === null ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                All
              </a>
              {tierList.map((t) => (
                <a
                  key={t.value}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setTierFilter(t.value); setPage(0); }}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${tierFilter === t.value ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  {t.label}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
      <div className="flex items-center py-2">
        <label className="flex items-center text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showExcluded}
            onChange={(e) => { setShowExcluded(e.target.checked); setPage(0); }}
            className="focus:ring-gray-500 h-4 w-4 text-gray-600 border-gray-300 rounded mr-2"
          />
          Show excluded listings
        </label>
      </div>
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto">
          <div className="py-2 align-middle flex min-w-full">
            <div className="shadow overflow-hidden min-w-full border-b border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Name
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                      Category
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                      Status
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                      Tier
                    </th>
                    <th scope="col" className="pl-3 py-3.5 sm:table-cell">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-300">
                  {listings.map(l => (
                    <tr className="hover:bg-gray-100 cursor-pointer" key={l.id} onClick={() => onNewListing(l)}>
                      <td className="pl-3 py-4 pr-3 flex-col">
                        <div className="flex items-top sm:items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary pt-2 text-center text-white relative">
                              {getInitials(l.name)}
                              {l.vipNetworkEnabled && (
                                <div className="absolute -top-1 -right-1 h-5 w-5 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center">
                                  <svg className="h-3 w-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="ml-4 sm:flex-col">
                            <div className="py-2 text-sm font-medium text-gray-900 flex items-center gap-2">
                              {l.name}
                              {l.isExcluded && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                  Excluded
                                </span>
                              )}
                              {l.vipNetworkEnabled && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gradient-to-r from-yellow-100 to-yellow-200 text-primary border border-yellow-300">
                                  VIP
                                </span>
                              )}
                            </div>
                              <dd className="py-2 text-sm font-medium sm:hidden">
                            <span>
                              {l.categories.map(c => {
                                return (
                                  <span key={c.id} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-green-800">
                                    {c.name}
                                  </span>
                                );
                              })}
                            </span>
                          </dd>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-6 py-4 whitespace-nowrap sm:table-cell">
                        {l.categories.map(c => {
                          return (
                            <span key={c.id} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-green-800">
                              {c.name}
                            </span>
                          );
                        })}
                      </td>
                      <td className="hidden px-6 py-4 whitespace-nowrap sm:table-cell">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusBadgeStyles[l.status] || 'bg-gray-100 text-gray-800'}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="hidden px-6 py-4 whitespace-nowrap sm:table-cell text-sm text-gray-900">
                        {getTierLabel(l.featureLevel)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`listings/${l.id}/edit`}>
                        <button className="items-center text-center w-16 py-2 my-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                          Edit
                        </button>
                      </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>

            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <NewListingForm onCancelClick={() => setShowCreateModal(false)} onNewListing={onNewListing} />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
